import React, { useState } from "react";
import {
  Box,
  Modal,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { api } from "../../services/api";

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [email, setEmail] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState<"request" | "confirm">("request");

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await api.auth.resetPassword(email);
      setStep("confirm");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send reset code"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);

    try {
      await api.auth.confirmPasswordReset(email, confirmationCode, newPassword);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setConfirmationCode("");
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
    setSuccess(false);
    setStep("request");
    onClose();
  };

  // Clear form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setEmail("");
      setConfirmationCode("");
      setNewPassword("");
      setConfirmPassword("");
      setError(null);
      setSuccess(false);
      setStep("request");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="forgot-password-modal"
      aria-describedby="forgot-password-form"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 400,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" component="h2">
            Reset Password
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {step === "request" && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter your email address and we'll send you a verification code to
              reset your password.
            </Typography>

            <Box
              component="form"
              onSubmit={handleRequestReset}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <TextField
                type="email"
                label="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                fullWidth
                size="small"
                inputProps={{
                  autoComplete: "off",
                  autoFill: "off",
                }}
              />

              {error && <Alert severity="error">{error}</Alert>}

              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                fullWidth
                sx={{ mt: 1 }}
              >
                {isLoading ? <CircularProgress size={20} /> : "Send Reset Code"}
              </Button>
            </Box>
          </>
        )}

        {step === "confirm" && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter the 6-digit verification code sent to your email and create
              a new password.
            </Typography>

            {success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Password reset successful! You can now log in with your new
                password.
              </Alert>
            ) : (
              <Box
                component="form"
                onSubmit={handleConfirmReset}
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                <TextField
                  type="text"
                  label="6-digit verification code"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                  required
                  disabled={isLoading}
                  fullWidth
                  size="small"
                  inputProps={{
                    maxLength: 6,
                    autoComplete: "off",
                    autoFill: "off",
                  }}
                />

                <TextField
                  type="password"
                  label="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  fullWidth
                  size="small"
                  inputProps={{
                    autoComplete: "new-password",
                    autoFill: "off",
                  }}
                />

                <TextField
                  type="password"
                  label="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  fullWidth
                  size="small"
                  inputProps={{
                    autoComplete: "new-password",
                    autoFill: "off",
                  }}
                />

                {error && <Alert severity="error">{error}</Alert>}

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  {isLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Reset Password"
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => setStep("request")}
                  disabled={isLoading}
                  fullWidth
                >
                  Back to Email
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
};
